import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ContainerOutlined,
  FileSearchOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Input, Button, notification } from "antd";
import { useToaster } from "@components";
import { useFormik } from "formik";
import * as Yup from "yup";

import { usersLoginPost } from "../../libs/apis";
import { useModulesBackupStore, useRouterStore } from "../../libs/stores";
import { LoadingFullPage } from "./Loading";

import Logo from "../images/logo.png";
import "../css/login.css";

export const Login = () => {
  const navigate = useNavigate();
  const toaster = useToaster();
  const tokens = localStorage.getItem(process.env.REACT_APP_TOKEN!);
  const setModules = useRouterStore((state) => state.setModules);
  const setModulesBackup = useModulesBackupStore(
    (state) => state.setModulesBackup
  );
  const setFirstName = useRouterStore((state) => state.setFirstName);
  const setProfileId = useRouterStore((state) => state.setProfileId);
  const setClientId = useRouterStore((state) => state.setClientId);
  const setClient = useRouterStore((state) => state.setClient);
  const setTypeDashboardCode = useRouterStore(
    (state) => state.setTypeDashboardCode
  );
  const setAvatar = useRouterStore((state) => state.setAvatar);
  const [isProcessing, setProcessing] = React.useState(false);
  const [continuo, setContinuo] = React.useState(false);

  React.useEffect(() => {
    // Si el usuario ya está autenticado, redirígelo
    if (tokens) {
      navigate("/dashboard", { replace: true });
    }
  }, [tokens, navigate]);

  React.useEffect(() => {
    if (continuo) {
      setContinuo(false);
      navigate("/dashboard", { replace: true });
    }
  }, [continuo]);

  const openNotification = (message: string) => {
    toaster.success({
      title: "Bienvenido",
      message,
      duration: 5000,
      placement: "topRight",
    });
  };

  const formik = useFormik({
    initialValues: {
      user: "",
      password: "",
    },
    validationSchema: Yup.object({
      user: Yup.string()
        .trim()
        .min(3, "Demasiado corto!")
        .max(30, "Demasiado largo!")
        .required("Requerido"),
      password: Yup.string()
        .trim()
        .min(3, "Demasiado corto!")
        .max(30, "Demasiado largo!")
        .required("Requerido"),
    }),
    onSubmit: async (values) => {
      try {
        setProcessing(true);
        const { data: dataUser } = await usersLoginPost(values);
        if (!dataUser) return;

        localStorage.setItem(
          process.env.REACT_APP_TOKEN!,
          JSON.stringify(dataUser)
        );
        openNotification(dataUser.user.fullName);
        // notification.success({
        //   message: "Bienvenido",
        //   description: dataUser.user.fullName,
        //   duration: 3,
        // });
        setModules(dataUser.user.modules);
        setModulesBackup(dataUser.user.modules);
        setClientId(dataUser.user.clientId);
        setClient(dataUser.user.client);
        setProfileId(dataUser.user.profileId);
        setAvatar(dataUser.user.avatar!);
        setFirstName(dataUser.user.firstName);
        setTypeDashboardCode(dataUser.user.typeDashboardCode!);
        setProcessing(false);
        formik.resetForm();
        setContinuo(true);
      } catch (err: any) {
        setProcessing(false);
        toaster.error({
          title: "Error",
          message: err.toString() ?? "Error, comuníquese con Sistemas",
          duration: 3,
        });
      }
    },
  });

  if (tokens) {
    return <LoadingFullPage />; // O puedes usar un spinner o componente de carga
  }

  return (
    <div className="login-container">
      <div className="login-bg-orb login-bg-orb--a" />
      <div className="login-bg-orb login-bg-orb--b" />
      <div className="login-bg-grid" />

      <div className="login-card">
        <aside className="login-brand">
          <img src={Logo} alt="NERON CODE" className="login-brand-logo" />
          <h1 className="login-brand-title">
            Control integral de tus <span>contenedores</span>
          </h1>
          <p className="login-brand-text">
            Gestiona exportaciones, inspecciones y trazabilidad de contenedores
            desde un solo lugar.
          </p>
          <ul className="login-features">
            <li>
              <ContainerOutlined /> Seguimiento de contenedores en tiempo real
            </li>
            <li>
              <SafetyCertificateOutlined /> Checkpoints, sellos y control de
              calidad
            </li>
            <li>
              <FileSearchOutlined /> Reportes y trazabilidad completa
            </li>
            <li>
              <TeamOutlined /> Gestión de usuarios, perfiles y proveedores
            </li>
          </ul>
        </aside>

        <section className="login-form-side">
          <form onSubmit={formik.handleSubmit} className="login-form">
            <h2>Inicio de sesión</h2>
            <p className="login-subtitle">
              Ingresa tus credenciales para continuar
            </p>

            <label className="login-label" htmlFor="user">
              Usuario
            </label>
            <Input
              id="user"
              name="user"
              size="large"
              autoComplete="username"
              prefix={<UserOutlined />}
              placeholder="Tu usuario"
              value={formik.values.user}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              status={formik.touched.user && formik.errors.user ? "error" : ""}
            />
            <p className="login-error">
              {formik.touched.user && formik.errors.user}
            </p>

            <label className="login-label" htmlFor="password">
              Contraseña
            </label>
            <Input.Password
              id="password"
              name="password"
              size="large"
              autoComplete="current-password"
              prefix={<LockOutlined />}
              placeholder="Tu contraseña"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              status={
                formik.touched.password && formik.errors.password ? "error" : ""
              }
            />
            <p className="login-error">
              {formik.touched.password && formik.errors.password}
            </p>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="login-submit"
              loading={isProcessing}
              disabled={isProcessing}
            >
              Entrar
            </Button>

            <div className="login-divider" />
            <span className="login-footer">
              NERON CODE · Control de contenedores
            </span>
          </form>
        </section>
      </div>
    </div>
  );
};
